import { setSelectedComponents } from '@/store/slices/assetStudioSlice';
import { dispatch } from '@/store/store';
import { useAppFilters, useActiveTab } from '@/hooks';
import { useActiveAssetsType } from '@/hooks/shared/useActiveAssetsType';
import { ASSET_STUDIO, FILTER_PARAMS } from '@/types/shared/navigation-tabs.enums';
import React, { useMemo } from 'react';
import CardTitle from '@/components/uiKit/Cards/CardTitle';
import { useComponentList } from './useComponentList';
import { formatAssetName } from '@/utils/shared';

export function useComponentSelector({
  components,
  currentComponents,
  asset,
  additionalCounters,
}: {
  components: any;
  currentComponents: any;
  asset?: any;
  additionalCounters?: any;
}) {
  const { setFilters, selectedComponent } = useAppFilters();
  const { isMaintenance, isSensorData, isVisualAI, isAlarms } = useActiveTab();
  const { isMobileAsset, isFixedAssets } = useActiveAssetsType();
  const variant: 'subcomponents' | 'maintenance' | 'base' = useMemo(
    () => (isMaintenance || isAlarms ? 'maintenance' : 'base'),
    [isMaintenance, isAlarms]
  );

  const { componentsList } = useComponentList(currentComponents, additionalCounters, asset);

  // a component must be selected, so we redirect to diagnostics upon asset change
  useMemo(() => {
    if ((isSensorData || isVisualAI) && selectedComponent == ASSET_STUDIO.ALL_COMPONENTS)
      setFilters({
        [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.DIAGNOSTICS,
      });
  }, [asset]);

  const defaultVal = useMemo(() => {
    return { components: selectedComponent ? [selectedComponent] : [ASSET_STUDIO.ALL_COMPONENTS] };
  }, [selectedComponent, components]);

  const handelFilterUpdate = (data: any) => {
    if (Array.isArray(data) && !data.includes(ASSET_STUDIO.ALL_COMPONENTS)) {
      setFilters({ [FILTER_PARAMS.SELECTED_COMPONENT]: ASSET_STUDIO.ALL_COMPONENTS });
      dispatch(
        setSelectedComponents({
          id: ASSET_STUDIO.ALL_COMPONENTS,
          data: components,
        })
      );
    } else {
      const item = components?.find((el: any) => el.component_id === data?.components?.[0]);
      if (item) {
        setFilters({
          [FILTER_PARAMS.SELECTED_COMPONENT]: data?.components?.[0],
        });
        dispatch(setSelectedComponents({ id: data?.components?.[0], data: [item] }));
      } else if (
        data?.components?.[0] === ASSET_STUDIO.ALL_COMPONENTS ||
        data?.components?.[0] === ASSET_STUDIO.UNASSIGNED
      ) {
        setFilters({
          [FILTER_PARAMS.SELECTED_COMPONENT]: data?.components?.[0],
        });
      }
    }
  };

  const cardTitle = useMemo(() => {
    if (!asset) return '';
    return <CardTitle text={formatAssetName(asset, isMobileAsset)} />;
  }, [isMobileAsset, asset]);

  //@TODO : check if needed here at all
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleItemClick = () => {};

  const uiUnits = componentsList?.units;
  const items = componentsList?.components;
  const groupKey = 'unit_ui_name';

  return { items, uiUnits, groupKey, defaultVal, cardTitle, handelFilterUpdate, variant, onItemClick: handleItemClick };
}
